// hooks/useRadiationEmployes.js
// ============================================================================
// RADIATION DES EMPLOYÉS : APPELS ET PERMISSIONS
// ============================================================================
//
// Un employé qui part est radié, jamais effacé (18 septembre 2026) : sa paie,
// ses avances, ses caisses et ses cours restent dans l'historique. Le serveur
// tranche (backend2/routes/employesRadiation.routes.js) ; les permissions lues
// ici évitent seulement de proposer ce qui serait refusé.
//
//   RESSOURCE_HUMAINES.RADIATION.CREATE     radier
//   RESSOURCE_HUMAINES.RADIATION.RESTORE    restaurer
//   RESSOURCE_HUMAINES.RADIATION.VIEW_LIST  voir les employés radiés
//   RESSOURCE_HUMAINES.EMPLOYES.DELETE      supprimer une fiche sans historique
// ============================================================================

import { getAuthHeaders } from '../services/api';
import { usePermissions } from './usePermissions';

const BASE = '/api/employes/radiation';

const CLES = {
  radier: 'RESSOURCE_HUMAINES.RADIATION.CREATE',
  restaurer: 'RESSOURCE_HUMAINES.RADIATION.RESTORE',
  voirRadies: 'RESSOURCE_HUMAINES.RADIATION.VIEW_LIST',
  supprimer: 'RESSOURCE_HUMAINES.EMPLOYES.DELETE'
};

export const usePermissionsRadiationEmployes = () => {
  const { detailedPermissions, loading } = usePermissions();
  const cles = new Set((detailedPermissions || []).map((p) => p.permission_key || p.key));

  return {
    peutRadier: cles.has(CLES.radier),
    peutRestaurer: cles.has(CLES.restaurer),
    peutVoirRadies: cles.has(CLES.voirRadies),
    peutSupprimer: cles.has(CLES.supprimer),
    chargement: Boolean(loading)
  };
};

// Toutes les réponses passent par ici : un refus métier (409, 403) porte un
// message à afficher tel quel, et devient une erreur.
const appeler = async (url, options = {}) => {
  const response = await fetch(url, { ...options, headers: getAuthHeaders() });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    const erreur = new Error(data.message || `Erreur ${response.status}`);
    erreur.code = data.code;
    erreur.status = response.status;
    throw erreur;
  }
  return data;
};

export const radiationEmployesApi = {
  motifs: async () => (await appeler(`${BASE}/motifs`)).data || [],
  radies: async () => (await appeler(`${BASE}/radies`)).data || [],
  apercu: async (employeId, donnees = {}) => (
    await appeler(`${BASE}/${employeId}/apercu`, { method: 'POST', body: JSON.stringify(donnees) })
  ).data,
  radier: (employeId, donnees) => appeler(`${BASE}/${employeId}/radier`, {
    method: 'POST',
    body: JSON.stringify(donnees)
  }),
  restaurer: (employeId, donnees = {}) => appeler(`${BASE}/${employeId}/restaurer`, {
    method: 'POST',
    body: JSON.stringify(donnees)
  }),
  supprimer: (employeId) => appeler(`/api/employes/${employeId}`, { method: 'DELETE' })
};

export default radiationEmployesApi;
